import { useId } from 'react';
import { useFormContext, type FieldError as FieldErrorType } from 'react-hook-form';
import formStyles from '~/styles/form.module.css';
import getWithDot from '~/helpers/getWithDot';
import useTranslation from '~/hooks/useTranslation';
import FormLabel from './FormLabel';
import FieldError from './FieldError';

type Props = {
  label: string;
  registerField: string;
  min?: string;
  max?: string;
  required?: boolean;
};

export default function DateField({
  label,
  registerField,
  min,
  max,
  required = false,
}: Props) {
  const fieldId = useId();
  const { register, formState: { errors } } = useFormContext();
  const translate = useTranslation();

  const minAge = 12;
  const maxAge = 100;

  const minDateBasedOnAge = new Date(new Date().setFullYear(new Date().getFullYear() - maxAge));
  const maxDateBasedOnAge = new Date(new Date().setFullYear(new Date().getFullYear() - minAge));

  const minDate = min || minDateBasedOnAge.toISOString().split('T')[0];
  const maxDate = max || maxDateBasedOnAge.toISOString().split('T')[0];

  const errorForField = getWithDot<FieldErrorType>(registerField, errors);

  return (
    <div>
      <FormLabel htmlFor={fieldId} required={required}>
        {label}
      </FormLabel>

      <input
        type="date"
        id={fieldId}
        className={formStyles.formControl}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...register(registerField, {
          required,
          min: min
            ? {
              value: min,
              message: translate('form.validation.minDate', { date: minDate }),
            }
            : undefined,
          max: max
            ? {
              value: max,
              message: translate('form.validation.maxDate', { date: maxDate }),
            }
            : undefined,
        })}
        min={minDate}
        max={maxDate}
        required={required}
        aria-invalid={errorForField ? 'true' : 'false'}
      />

      {errorForField && (
        <FieldError error={errorForField} />
      )}
    </div>
  );
}
